
import { RealtimeChannel } from '@supabase/supabase-js';
import { logErrorToast } from '@/utils/toastLogger';

// Constants for error handling and backoff
const MAX_REALTIME_ERRORS = 3; // Max consecutive errors before pausing attempts
const BACKOFF_DELAY_BASE = 2000; // Base delay (2 seconds)
const MAX_BACKOFF_DELAY = 30000; // Max delay (30 seconds)

/**
 * Calculates exponential backoff delay with jitter
 * @param attempt Current attempt number
 * @returns Delay in milliseconds
 */
export const calculateBackoff = (attempt: number): number => {
  // Exponential backoff with jitter (±20%)
  const baseDelay = Math.min(
    MAX_BACKOFF_DELAY,
    BACKOFF_DELAY_BASE * Math.pow(2, attempt)
  );
  const jitter = baseDelay * 0.2 * (Math.random() * 2 - 1); // ±20% jitter
  return Math.floor(baseDelay + jitter);
};

/**
 * Creates and manages a Supabase realtime subscription for saved jobs
 */
export interface RealtimeSubscriptionProps {
  supabase: any;  // Supabase client
  userId: string;
  onDataChange: () => void;
  onError?: (error: any) => void;
}

export const useSupabaseRealtimeSubscription = ({
  supabase,
  userId,
  onDataChange,
  onError
}: RealtimeSubscriptionProps): {
  subscribe: () => void;
  unsubscribe: () => void;
  channel: RealtimeChannel | null;
  errorCount: number;
} => {
  let channel: RealtimeChannel | null = null;
  let errorCount = 0;
  let isSubscribed = false;

  const subscribe = () => {
    if (isSubscribed || !userId || errorCount >= MAX_REALTIME_ERRORS) return;

    try {
      const topic = `saved_jobs_user_${userId}`;
      
      // Clear any existing channel first
      if (channel) {
        unsubscribe();
      }
      
      channel = supabase
        .channel(topic)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'saved_jobs',
            filter: `user_id=eq.${userId}`,
          },
          (payload: any) => {
            console.log('[RealtimeSubscription] Saved jobs changed:', payload);
            errorCount = 0; // Reset error count on successful message
            onDataChange();
          }
        )
        .subscribe((status: string, err?: any) => {
          if (status === 'SUBSCRIBED') {
            console.log('[RealtimeSubscription] Successfully subscribed to', topic);
            errorCount = 0; // Reset error count on successful subscription
            isSubscribed = true;
          }
          
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            const errorMessage = err?.message || 'Realtime subscription error';
            console.error('[RealtimeSubscription] Error on', topic, err);
            errorCount++;
            isSubscribed = false;

            // Only notify on first few errors to prevent toast spam
            if (errorCount <= 2) {
              if (onError) onError(err);
              else logErrorToast(errorMessage, err);
            } else {
              // Just log silently
              console.warn(`[RealtimeSubscription] Suppressed error notification (${errorCount})`, errorMessage);
            }
            
            // If max errors reached, stop trying
            if (errorCount >= MAX_REALTIME_ERRORS) {
              console.error(`[RealtimeSubscription] Max errors (${MAX_REALTIME_ERRORS}) reached, stopping realtime subscription attempts`);
            }
          }
        });
    } catch (error) {
      console.error('[RealtimeSubscription] Setup error:', error);
      errorCount++;
      if (onError) onError(error);
    }
  };

  const unsubscribe = () => {
    if (channel) {
      try {
        supabase.removeChannel(channel)
          .catch((err: any) => console.error('[RealtimeSubscription] Error removing channel:', err));
        channel = null;
        isSubscribed = false;
      } catch (err) {
        console.error('[RealtimeSubscription] Error during unsubscribe:', err);
      }
    }
  };

  return {
    subscribe,
    unsubscribe,
    channel,
    errorCount
  };
};
